import { useMutation, useQueryClient } from '@tanstack/react-query';

import { JobApplication } from '@restworld/utility-types';
import { UpdateJobApplicationParams, useWorkerJobPositionsService } from '@restworld/data-services';

import { useDiscardJobApplicationQueryKey, useEmployerJobApplicationQueryKey } from './use-employer-job-applications';

function useUpdateJobApplication() {
  const queryKey = useEmployerJobApplicationQueryKey();
  const discardedQueryKey = useDiscardJobApplicationQueryKey();

  const workerJobpositionsService = useWorkerJobPositionsService();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (params: UpdateJobApplicationParams) => workerJobpositionsService.updateJobApplication(params),
    onMutate(variables: UpdateJobApplicationParams) {
      queryClient.cancelQueries({ queryKey });
      const prevData = queryClient.getQueryData(queryKey) as JobApplication[];
      // Optimistically update the cache with the new value
      queryClient.setQueryData(queryKey, (oldData: JobApplication[]) => {
        if (variables.params.status_key === 'worker_jp_discarded') {
          return oldData.filter((jobApplication) => jobApplication.job_position_id !== variables.jobPositionId);
        }
        return oldData.map((jobApplication) => {
          if (jobApplication.worker_id === variables.workerId) {
            return {
              ...jobApplication,
              ...variables.params,
            };
          }
          return jobApplication;
        });
      });
      // when user discard the candidate, we need to add it to the discarded job application list
      if (variables.params.status_key === 'worker_jp_discarded') {
        const discardedCandidate = prevData?.find((jobApplication) => jobApplication.worker_id === variables.workerId);
        queryClient.setQueryData(discardedQueryKey, (oldData: JobApplication[]) => [
          ...oldData,
          ...(discardedCandidate
            ? [
                {
                  ...discardedCandidate,
                  ...variables.params,
                },
              ]
            : []),
        ]);
      }
      // Return the previous value in case of rollback
      return { prevData };
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [queryKey, discardedQueryKey] });
    },
    onError: (_err, prevApplication, context) => {
      if (context?.prevData) {
        queryClient.setQueryData(queryKey, context.prevData);
      }
    },
  });
  return mutation;
}

export function useUpdateDiscardedJobApplication() {
  const queryKey = useDiscardJobApplicationQueryKey();
  const queryKeyJobApplication = useEmployerJobApplicationQueryKey();

  const workerJobpositionsService = useWorkerJobPositionsService();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (params: UpdateJobApplicationParams) => workerJobpositionsService.updateJobApplication(params),
    onMutate(variables: UpdateJobApplicationParams) {
      queryClient.cancelQueries({ queryKey });
      const prevData = queryClient.getQueryData(queryKey) as JobApplication[];
      // Optimistically update the cache with the new value
      queryClient.setQueryData(queryKey, (oldData: JobApplication[]) =>
        oldData.filter((jobApplication) => jobApplication.worker_id !== variables.workerId)
      );
      // when user re add the discarded candidate, we need to add it back to the job application list
      if (variables.params.status_key === 'worker_jp_employer_interested') {
        const reAddedCandidate = prevData?.find((jobApplication) => jobApplication.worker_id === variables.workerId);
        queryClient.setQueryData(queryKeyJobApplication, (oldData: JobApplication[]) => {
          return [
            ...oldData,
            ...(reAddedCandidate
              ? [
                  {
                    ...reAddedCandidate,
                    ...variables.params,
                  },
                ]
              : []),
          ];
        });
      }
      // Return the previous value in case of rollback
      return { prevData };
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [queryKey, queryKeyJobApplication] });
    },
    onError: (_err, prevApplication, context) => {
      if (context?.prevData) {
        queryClient.setQueryData(queryKey, context.prevData);
      }
    },
  });
  return mutation;
}
export default useUpdateJobApplication;

export function useUpdateJobApplicationMutation() {
  const workerJobpositionsService = useWorkerJobPositionsService();

  const mutation = useMutation({
    mutationFn: (params: UpdateJobApplicationParams) => workerJobpositionsService.updateJobApplication(params),
  });
  return mutation;
}
